import { useCallback } from 'react'
import { excludedSourcesFor, type RequestedSupplements, type SupplementSource } from './requestMapping'
import { type BoxTruckUiElement, trackBoxTruckUiInteraction } from '../utils/analytics'
import type { BoxTruckTab } from './useBoxTruckTabs'

/** The board's per-tab hidden-source chips: a Set of source keys per board key. */
type HiddenSourcesByTab = Record<string, Set<string>>

/**
 * One switch's behaviour for a gated feed — the board's DAT and BEON toggles are this function twice.
 *
 * They differ only in which source they name, which preference they persist and which event they report;
 * everything else is bookkeeping that has to stay identical, and would be exactly what a second hand-written
 * copy drifted away from. A third supplement is one more `makeToggle` call.
 *
 * `requested` is passed to the toggle rather than read inside it, so a caller that knows something the state
 * does not yet — the connect flow, which has just made BEON requestable — can say so instead of depending on
 * a setter having landed first.
 */
export interface GatedSourceToggleBoard {
    /** The request gate. A REF, not state: switching a feed on re-fetches from inside the same handler,
     *  before React has re-rendered with the new value. */
    excludedSourcesRef: { current: ReadonlySet<string> }
    /** Drop a gated feed's stale "keep paginating" flag. */
    forgetGatedSource: (source: string) => void
    /** Merge a newly-asked-for feed into the board already on screen. */
    revealGatedSource: (source: string) => void
    dropSnapshot: (tabId: string) => void
    activeTabId: string
    /** A getter, not the ref: the board declares `tabsRef` further down its body than this hook is called,
     *  and a closure reads it when the toggle runs rather than when the board wires it up. */
    getTabs: () => readonly BoxTruckTab[]
    /** Whether the active tab has run a search, or is still on its "New search" state. */
    activeTabSearchedRef: { readonly current: boolean }
    setHiddenSourcesByTab: (update: (prev: HiddenSourcesByTab) => HiddenSourcesByTab) => void
    unhideSourceOnEveryBoard: (prev: HiddenSourcesByTab, source: string) => HiddenSourcesByTab
}

export type GatedSourceToggle = (enabled: boolean, requested: RequestedSupplements) => void

export const useGatedSourceToggle = (
    board: GatedSourceToggleBoard
): ((source: SupplementSource, element: BoxTruckUiElement, persist: (enabled: boolean) => void) => GatedSourceToggle) => {
    const {
        excludedSourcesRef,
        forgetGatedSource,
        revealGatedSource,
        dropSnapshot,
        activeTabId,
        getTabs,
        activeTabSearchedRef,
        setHiddenSourcesByTab,
        unhideSourceOnEveryBoard,
    } = board

    return useCallback(
        (source: SupplementSource, element: BoxTruckUiElement, persist: (enabled: boolean) => void): GatedSourceToggle =>
            (enabled, requested) => {
                persist(enabled)
                excludedSourcesRef.current = excludedSourcesFor(requested)
                trackBoxTruckUiInteraction(element, enabled ? 'on' : 'off')
                if (!enabled) {
                    // No longer requested, so nothing will ever correct its stale "keep paginating" flag. Drop
                    // it, or the scroller keeps asking for pages of a feed that will not come.
                    forgetGatedSource(source)
                    return
                }
                // Switched on: the operator is asking FOR these rows, so clear a chip that would hide them the
                // moment they land. Ahead of the searched-tab check because an unsearched tab searches later,
                // and a chip left set would hide the feed then instead.
                setHiddenSourcesByTab((prev) => unhideSourceOnEveryBoard(prev, source))
                if (!activeTabSearchedRef.current) return
                // The ACTIVE board gets the feed merged into it — pages already scrolled are kept.
                revealGatedSource(source)
                // Every OTHER tab holds a frozen board searched while this feed was off, so it has none of its
                // rows, and restoring that snapshot would not re-request them. Dropping their caches makes
                // switching to one re-search instead of restoring a board the operator can no longer fix.
                for (const tab of getTabs()) if (tab.id !== activeTabId) dropSnapshot(tab.id)
            },
        [
            excludedSourcesRef,
            forgetGatedSource,
            revealGatedSource,
            dropSnapshot,
            activeTabId,
            getTabs,
            activeTabSearchedRef,
            setHiddenSourcesByTab,
            unhideSourceOnEveryBoard,
        ]
    )
}
